import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '@/lib/firebase';

interface Availability {
  available: boolean;
}

export interface AvailabilityDocument {
  id: string;
  date: string;
  availabilities: {
    [key: string]: Availability;
  };
}

// Cache for storing availability data
let availabilityCache: AvailabilityDocument[] | null = null;

export const getAvailabilities = async (): Promise<AvailabilityDocument[]> => {
  try {
    // Return cached data if available
    if (availabilityCache) {
      return availabilityCache;
    }

    const response = await fetch(`${import.meta.env.VITE_EMAIL_SERVER}/booking/get_availabilities`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch availabilities');
    }

    const data = await response.json();
    //console.log(data);
    // Cache the data
    availabilityCache = data;
    return data;
  } catch (error) {
    console.error('Error fetching availabilities:', error);
    throw error;
  }
};

// Helper function to check if a specific time slot is available
export const isTimeSlotAvailable = async (date: string, time: string): Promise<boolean> => {
  try {
    const availabilities = await getAvailabilities();
    const availabilityDoc = availabilities.find(doc => doc.date === date);
    if (!availabilityDoc) return false;
    
    const timeSlot = availabilityDoc.availabilities[time];
    return timeSlot ? timeSlot.available : false;
  } catch (error) {
    console.error('Error checking time slot availability:', error);
    throw error;
  }
};

// Helper function to get all available time slots for a specific date
export const getAvailableTimeSlots = async (date: string): Promise<string[]> => {
  try {
    const availabilities = await getAvailabilities();
    const availabilityDoc = availabilities.find(doc => doc.date === date);
    if (!availabilityDoc) return [];
    
    return Object.entries(availabilityDoc.availabilities)
      .filter(([_, availability]) => availability.available)
      .map(([time]) => time)
      .sort();
  } catch (error) {
    console.error('Error fetching available time slots:', error);
    throw error;
  }
};

// Function to clear the cache (useful when you need to force a refresh)
export const clearAvailabilityCache = () => {
  availabilityCache = null;
};
